import { useEffect, useState } from "react";
import { IOrderDetail } from "../../models/IOrderDetail";
import { getOrderDetailById, UpdateOrderStatus } from "../../services/order";
import {
  IPhoneOrderLedgerItem, PHONE_ORDER_NEXT_STEP as NEXT_STEP, PHONE_ORDER_STATUS, phonePaymentLabel,
} from "../models/IPhoneOrder";
import { phoneErrorMessage } from "../services/phoneOrderService";

const STEPS = [
  { id: PHONE_ORDER_STATUS.preparing, label: "Hazırlanıyor", icon: "bi-fire" },
  { id: PHONE_ORDER_STATUS.onTheWay, label: "Yolda", icon: "bi-scooter" },
  { id: PHONE_ORDER_STATUS.delivered, label: "Teslim edildi", icon: "bi-house-check-fill" },
];

export const STATUS_ERROR: Record<string, string> = {
  OrderStatusTransitionNotAllowed: "Sipariş bu duruma bu aşamadan geçirilemez. Listeyi yenileyin.",
  OrderStatusUnknown: "Geçersiz sipariş durumu.",
  OrderNotFound: "Sipariş bulunamadı.",
  OrderAccessDenied: "Bu siparişi görme yetkiniz yok.",
  RestourantAccessDenied: "Bu siparişte işlem yapma yetkiniz yok.",
};

function money(n: number, symbol?: string | null): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

interface Props {
  order: IPhoneOrderLedgerItem;
  onClose: () => void;
  /** Durum değişince liste yenilensin diye. */
  onChanged: () => void;
  onFixPayment: (order: IPhoneOrderLedgerItem) => void;
}

export default function PhoneOrderDetailModal({ order, onClose, onChanged, onFixPayment }: Props) {
  const [detail, setDetail] = useState<IOrderDetail | null>(null);
  const [loadError, setLoadError] = useState("");
  const [statusId, setStatusId] = useState(order.orderStatusId.toLowerCase());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    getOrderDetailById(order.orderId)
      .then((res) => setDetail(res.data))
      .catch((err) => setLoadError(phoneErrorMessage(err, STATUS_ERROR, "Sipariş içeriği alınamadı.")));
  }, [order.orderId]);

  const cancelled = order.isCancelled || statusId === PHONE_ORDER_STATUS.cancelled;
  const next = cancelled ? undefined : NEXT_STEP[statusId];
  const canCancel = !cancelled && statusId === PHONE_ORDER_STATUS.preparing;
  const stepIndex = STEPS.findIndex((s) => s.id === statusId);

  const changeStatus = (to: string, reason?: string) => {
    setBusy(true);
    setError("");
    UpdateOrderStatus(order.orderId, to, reason)
      .then(() => {
        setStatusId(to);
        setConfirmCancel(false);
        onChanged();
      })
      .catch((err) => setError(phoneErrorMessage(err, STATUS_ERROR, "Durum güncellenemedi.")))
      .finally(() => setBusy(false));
  };

  const lines = detail?.foodPreOrder ?? [];
  const symbol = detail?.currency?.currencyDisplayText ?? order.currencyDisplayText;

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dinein-modal po-detail">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title">
              <i className="bi bi-receipt me-2"></i>{order.orderCode}
            </div>
            <div className="dinein-modal__sub">{order.customerFullName} · {order.phoneNumber}</div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={busy}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          {/* Durum çizgisi */}
          {cancelled ? (
            <div className="dinein-alert dinein-alert--error">
              <i className="bi bi-x-octagon-fill"></i><span>Bu sipariş iptal edildi.</span>
            </div>
          ) : (
            <div className="po-steps">
              {STEPS.map((s, i) => (
                <div key={s.id}
                  className={`po-steps__item${i < stepIndex ? " po-steps__item--done" : ""}${i === stepIndex ? " po-steps__item--now" : ""}`}>
                  <span className="po-steps__dot"><i className={`bi ${i < stepIndex ? "bi-check-lg" : s.icon}`}></i></span>
                  <span className="po-steps__label">{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Mutfak fişi */}
          <div className="po-ticket">
            <div className="po-ticket__head">
              <span><i className="bi bi-fire me-1"></i>Hazırlanacaklar</span>
              {detail && <span>{lines.reduce((s, l) => s + l.foodQuantity, 0)} adet</span>}
            </div>

            {loadError ? (
              <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{loadError}</span></div>
            ) : !detail ? (
              <div className="dinein-center"><span className="spinner-border spinner-border-sm" /></div>
            ) : (
              <ul className="po-ticket__lines">
                {lines.map((l) => (
                  <li key={l.id} className="po-ticket__line">
                    <span className="po-ticket__qty">{l.foodQuantity}×</span>
                    <div className="po-ticket__body">
                      <div className="po-ticket__name">{l.food?.foodNameTr ?? "Ürün"}</div>
                      {l.menuUpgradeNotes && (
                        <div className="po-ticket__extra"><i className="bi bi-plus-circle me-1"></i>{l.menuUpgradeNotes}</div>
                      )}
                      {l.foodOrderNote && (
                        <div className="po-ticket__note"><i className="bi bi-chat-left-text me-1"></i>{l.foodOrderNote}</div>
                      )}
                    </div>
                    <span className="po-ticket__price">{money(l.lineTotal, symbol)}</span>
                  </li>
                ))}
              </ul>
            )}

            {order.orderNote && (
              <div className="po-ticket__order-note">
                <i className="bi bi-sticky-fill me-2"></i>{order.orderNote}
              </div>
            )}
          </div>

          {/* Teslimat ve ödeme */}
          <div className="po-detail__info">
            <div className="po-detail__info-row">
              <i className="bi bi-geo-alt-fill"></i>
              <span>{order.deliveryAddress}</span>
            </div>
            <div className="po-detail__info-row">
              <i className="bi bi-wallet2"></i>
              <span>
                {phonePaymentLabel(order.paymentMethodId, order.paymentMethodNameTr)}
                {!order.isPaymentMethodSpecified && <em className="po-detail__warn"> — henüz girilmedi</em>}
              </span>
              {!cancelled && (
                <button className="dinein-link" onClick={() => onFixPayment(order)}>
                  {order.isPaymentMethodSpecified ? "Değiştir" : "Gir"}
                </button>
              )}
            </div>
            <div className="po-detail__info-row">
              <i className="bi bi-person-badge"></i>
              <span>Kurye {order.hasDriverAssigned ? "atandı" : "atanmadı"}</span>
            </div>
            <div className="po-detail__total">
              <span>Toplam</span>
              <strong>{money(order.totalAmount, order.currencyDisplayText)}</strong>
            </div>
          </div>

          {error && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}

          {confirmCancel && (
            <div className="po-cancel">
              <div className="po-cancel__title">Sipariş iptal edilsin mi?</div>
              <input className="form-control" value={cancelReason} maxLength={250} disabled={busy}
                placeholder="İptal nedeni (opsiyonel)" onChange={(e) => setCancelReason(e.target.value)} />
              <div className="po-cancel__actions">
                <button className="dinein-btn dinein-btn--ghost" onClick={() => setConfirmCancel(false)} disabled={busy}>Vazgeç</button>
                <button className="dinein-btn dinein-btn--danger" disabled={busy}
                  onClick={() => changeStatus(PHONE_ORDER_STATUS.cancelled, cancelReason.trim() || undefined)}>
                  {busy ? <span className="spinner-border spinner-border-sm" /> : "Evet, iptal et"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="dinein-modal__foot po-detail__foot">
          {canCancel && !confirmCancel && (
            <button className="dinein-btn dinein-btn--ghost po-detail__cancel" onClick={() => setConfirmCancel(true)} disabled={busy}>
              <i className="bi bi-x-circle me-1"></i>İptal Et
            </button>
          )}
          <span style={{ flex: 1 }} />
          {next ? (
            <button className="dinein-btn dinein-btn--primary dinein-btn--lg" onClick={() => changeStatus(next.to)} disabled={busy}>
              {busy && !confirmCancel
                ? <><span className="spinner-border spinner-border-sm me-1" />Güncelleniyor...</>
                : <><i className={`bi ${next.icon} me-1`}></i>{next.label}</>}
            </button>
          ) : (
            <button className="dinein-btn dinein-btn--ghost" onClick={onClose}>Kapat</button>
          )}
        </div>
      </div>
    </div>
  );
}
